/**
 * Reading the orientation sweep BETWEEN its grid points.
 *
 * The sweep is stepped -- 5 degrees of tilt and 10 of azimuth on the quick
 * preview, finer once Optimize is pressed. The first version of the mini-plots
 * answered every question by snapping to the nearest grid point, and that is
 * how a project at 28 deg tilt / 177 deg azimuth was drawn at 30 / 175 and
 * reported as "0.00% below the optimum": the rounding had moved it onto the
 * optimum, so the figure was measuring the rounding rather than the design.
 *
 * The same arithmetic lives in BESS/Tools/Met_Tools.py, which is what the API
 * and the report use. It is repeated here only so the curves follow the tilt
 * field without a round trip on every keystroke -- and pv/orientation.test.jsx
 * checks this module against the backend's own answers on a shared fixture, so
 * the pair cannot quietly disagree. Sharing a helper while each caller keeps
 * its own recipe is exactly how the web loss diagram and the PDF one drew
 * different pictures for months.
 */

/** The two grid lines that straddle a target, and the blend between them. */
export function bracket(values, target) {
  const t = Number(target);
  let low = values[0];
  let high = values[values.length - 1];
  for (const v of values) {
    if (v <= t && v > low) low = v;
    if (v >= t && v < high) high = v;
  }
  if (!(t >= values[0])) low = high = values[0];
  else if (!(t <= values[values.length - 1])) low = high = values[values.length - 1];
  if (high === low) return { low, high, blend: 0 };
  return { low, high, blend: (t - low) / (high - low) };
}

/**
 * Bilinear interpolation of one field over the tilt x azimuth grid.
 * Falls back to the nearest point on a ragged grid: a hole in the chart is
 * worse than a point that is a step away and says so.
 */
export function valueAt(grid, tilt, azimuth, key = "kwh_per_kwdc") {
  const tilts = [...new Set(grid.map((g) => g.tilt))].sort((a, b) => a - b);
  const azimuths = [...new Set(grid.map((g) => g.azimuth))].sort((a, b) => a - b);
  return valueAtOn(grid, tilts, azimuths, tilt, azimuth, key);
}

function valueAtOn(grid, tilts, azimuths, tilt, azimuth, key) {
  const t = bracket(tilts, tilt);
  const a = bracket(azimuths, azimuth);
  const at = (tv, av) =>
    grid.find((g) => g.tilt === tv && g.azimuth === av)?.[key];
  const corners = [
    at(t.low, a.low), at(t.high, a.low),
    at(t.low, a.high), at(t.high, a.high),
  ];
  if (corners.some((c) => c == null)) {
    const nearest = grid.reduce((best, g) => {
      const d = Math.abs(g.tilt - tilt) + Math.abs(g.azimuth - azimuth);
      return best && best.d <= d ? best : { d, g };
    }, null);
    return nearest?.g?.[key] ?? null;
  }
  const low = corners[0] + (corners[1] - corners[0]) * t.blend;
  const high = corners[2] + (corners[3] - corners[2]) * t.blend;
  return low + (high - low) * a.blend;
}

/**
 * The two curves and the "you are here" value, all read at the SAME
 * orientation -- so the marker is a point of the curve that is drawn rather
 * than a second calculation that lands near it.
 */
// Number(null) and Number("") are both 0, so a plain isFinite check treats an
// unset tilt as a flat panel and silently slices the curve at 0 degrees. This
// has now been the same bug in three separate places in this codebase.
const asNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const out = Number(value);
  return Number.isFinite(out) ? out : null;
};

export function sliceOrientationGrid(grid, { tilt, azimuth, key = "kwh_per_kwdc" } = {}) {
  if (!Array.isArray(grid) || grid.length === 0) return null;
  if (asNumber(tilt) === null || asNumber(azimuth) === null) return null;

  const tilts = [...new Set(grid.map((g) => g.tilt))].sort((a, b) => a - b);
  const azimuths = [...new Set(grid.map((g) => g.azimuth))].sort((a, b) => a - b);

  return {
    tiltPoints: tilts.map((t) => ({
      x: t,
      y: valueAtOn(grid, tilts, azimuths, t, azimuth, key),
    })),
    azimuthPoints: azimuths.map((a) => ({
      x: a,
      y: valueAtOn(grid, tilts, azimuths, tilt, a, key),
    })),
    current: valueAtOn(grid, tilts, azimuths, tilt, azimuth, key),
  };
}

export default sliceOrientationGrid;
